import * as React from 'react';
import { Outlet, Route, Routes } from '@leagueplatform/routing';

import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';

import {
  LandingPage,
  CareCollectionsRoutes,
  CareDiscoveryRoutes,
  HealthJourneyRoutes,
  HealthNeedPages,
  HealthProfileRoutes,
  Home,
  OnboardingRoutesWithProvider,
  WalletRoutes,
  ClaimsRoutes,
} from 'components/LazyRoutes';

import ErrorPage from 'pages/Error404';
import AboutCignaHealthcare from 'pages/about-cigna/about-cigna.page';
import FormsCenterLandingPage from 'pages/forms-center/forms-center-landing-page.page';
import ProfileSettingsPage from 'pages/profile-settings/profile-settings.page';
import AuthorizationPage from 'pages/authorization/learn-about-authorization.page';
import SecuritySettingsPage from 'pages/security-settings/security-settings.page';
import CustomerSupportPage from 'pages/customer-support/customer-support.page';
import PDPPremiumPaymentPage from 'pages/premium-payment/pdp-premium-payment.page';
import SupplementalPremiumPaymentPage from 'pages/premium-payment/supplemental-premium-payment.page';
import MedicarePrescriptionPaymentPlanPage from 'pages/premium-payment/medicare-prescription-payment-plan.page';
import PremiumPaymentHistoryPage from 'pages/premium-payment/premium-payment-history.page';
import AutomaticPaymentsSetupPage from 'pages/premium-payment/automatic-payment-setup.page';
import PaymentCompleted from 'pages/premium-payment/payment-completed.page';
import { APP_MANAGED_ROUTES } from 'common/constants';
import { useAppRoutes } from 'hooks/use-app-routes.hook';
import { AuthSessionContainer } from './auth-session-container/auth-session-container';
import { OnboardingWrapper } from './onboarding/onboarding-wrapper';
import {
  AuthSignIn,
  AuthStartImpersonationSession,
} from './auth/auth-sign-in.component';
import { AuthSignOut } from './auth/auth-sign-out.component';
import { HomeRedirect } from './home-redirect';

const AuthRoutesElement = () => (
  <AuthSessionContainer>
    <OnboardingWrapper>
      <Outlet />
    </OnboardingWrapper>
  </AuthSessionContainer>
);

function AppRoutes() {
  const { isFormCenterAvailable } = useAppRoutes();

  return (
    <Routes>
      {/* If the user directly goes directly to an SSO route we cannot handle the SSO flow because */}
      {/* A user interaction is required to prevent the ad-blocker from blocking the SSO new tab flow.   */}
      <Route path="/sso/vendors/*" element={<HomeRedirect />} />
      <Route
        path={`${APP_MANAGED_ROUTES.SIGN_IN}/*`}
        element={<AuthSignIn />}
      />
      <Route
        path={`${APP_MANAGED_ROUTES.SIGN_OUT}/*`}
        element={<AuthSignOut />}
      />
      <Route
        path="/start-login/*"
        element={<AuthStartImpersonationSession />}
      />
      <Route path="/" element={<LandingPage />} />
      <Route element={<AuthRoutesElement />}>
        <Route path={APP_MANAGED_ROUTES.HOME} element={<Home />} key="home" />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory)}/*`}
          element={<HealthJourneyRoutes />}
          key="health-journey"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`}
          element={<CareCollectionsRoutes />}
          key="care-collections"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`}
          element={<CareDiscoveryRoutes />}
          key="care-discovery"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthNeedPages)}/*`}
          element={<HealthNeedPages />}
          key="health-needs"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.wallet)}/*`}
          element={<WalletRoutes />}
          key="wallet"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`}
          element={<HealthProfileRoutes />}
          key="health-profile"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.onboarding)}/*`}
          element={<OnboardingRoutesWithProvider contentfulId="" tenantId="" />}
          key="onboarding"
        />
        <Route
          path={`${getModulePath(LEAGUE_MODULE_NAMES.claims)}/*`}
          element={<ClaimsRoutes />}
          key="claims"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.ABOUT_CIGNA_ROUTE}/*`}
          element={<AboutCignaHealthcare />}
          key="about"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.PROFILE_SETTINGS}/*`}
          element={<ProfileSettingsPage />}
          key="profile-settings"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.SECURITY_SETTINGS}/*`}
          element={<SecuritySettingsPage />}
          key="security-settings"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.LEARN_ABOUT_AUTHORIZATION}/*`}
          element={<AuthorizationPage />}
          key="authorization"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.CUSTOMER_SUPPORT}/*`}
          element={<CustomerSupportPage />}
          key="customer-support"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.PDP_PREMIUM_PAYMENT}/*`}
          element={<PDPPremiumPaymentPage />}
          key="pdp-premium-payment"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.SUPPLEMENTAL_PREMIUM_PAYMENT}/*`}
          element={<SupplementalPremiumPaymentPage />}
          key="supplemental-premium-payment"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.MEDICARE_PRESCRIPTION_PAYMENT_PLAN}/*`}
          element={<MedicarePrescriptionPaymentPlanPage />}
          key="medicare-prescription-payment-plan"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.PREMIUM_PAYMENT_HISTORY}/*`}
          element={<PremiumPaymentHistoryPage />}
          key="premium-payment-history"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.PAYMENT_COMPLETED}/*`}
          element={<PaymentCompleted />}
          key="payment-completed"
        />
        <Route
          path={`${APP_MANAGED_ROUTES.AUTOMATIC_PAYMENT_SETUP}/*`}
          element={<AutomaticPaymentsSetupPage />}
          key="automatic-payment-setup"
        />

        {isFormCenterAvailable ? (
          <Route
            path={`${APP_MANAGED_ROUTES.FORMS_CENTER_ROUTE}/*`}
            element={<FormsCenterLandingPage />}
            key="forms-center"
          />
        ) : null}
        <Route element={<ErrorPage />} path="/*" />
      </Route>
    </Routes>
  );
}

export default AppRoutes;
